import { useEffect, useState } from 'react'
import { Regimen } from '../../../../../types/Regimen'

interface AmalgamAdjustmentsProps {
  dentalProcedures: Regimen[]
  isSaved: boolean
  currentNumAmalgamFillings: string
  amalgamTotalTeeth: string
  dentalItems: { [itemId: string]: string }
}

export const useAmalgamAdjustments = ({
  dentalProcedures,
  isSaved,
  currentNumAmalgamFillings,
  amalgamTotalTeeth,
  dentalItems,
}: AmalgamAdjustmentsProps) => {
  const [adjustedAmalgamFillings, setAdjustedAmalgamFillings] = useState(
    currentNumAmalgamFillings
  )
  const [adjustedTotalAmalgamFillings, setAdjustedTotalAmalgamFillings] =
    useState(amalgamTotalTeeth)
  const [hasAmalgamAdjustments, setHasAmalgamAdjustments] = useState(false)
  const [hasTotalAmalgamAdjustments, setHasTotalAmalgamAdjustments] =
    useState(false)

  useEffect(() => {
    if (isSaved && dentalProcedures.length > 0) {
      let currentFillings = Number(currentNumAmalgamFillings) || 0
      let totalFillings = Number(amalgamTotalTeeth) || 0
      let hasAdjustment = false
      let hasTotalAdjustment = false

      // Process all dental procedures
      dentalProcedures.forEach((procedure) => {
        const lastUsage = procedure.usages[procedure.usages.length - 1]
        const procedureId = lastUsage?.fields?.dentalProcedureId
        const procedureName = dentalItems[procedureId]?.toLowerCase() || ''

        // Check for procedures that reduce amalgam count
        if (
          procedureName.includes('amalgam filling replaced with composite') ||
          procedureName.includes('root canal tooth pulled with amalgam') ||
          procedureName.includes('tooth pulled with amalgam') ||
          procedureName.includes('amalgam removal')
        ) {
          currentFillings = Math.max(0, currentFillings - 1)
          hasAdjustment = true
        }

        // Check for procedures that increase amalgam count
        if (
          procedureName.includes('amalgam filling placed') ||
          procedureName.includes('amalgam filling replaced with amalgam')
        ) {
          currentFillings += 1
          totalFillings += 1
          hasAdjustment = true
          hasTotalAdjustment = true
        }
      })

      setAdjustedAmalgamFillings(currentFillings.toString())
      setAdjustedTotalAmalgamFillings(totalFillings.toString())
      setHasAmalgamAdjustments(hasAdjustment)
      setHasTotalAmalgamAdjustments(hasTotalAdjustment)
    } else {
      // If not saved or no procedures, use the original values
      setAdjustedAmalgamFillings(currentNumAmalgamFillings)
      setAdjustedTotalAmalgamFillings(amalgamTotalTeeth)
      setHasAmalgamAdjustments(false)
      setHasTotalAmalgamAdjustments(false)
    }
  }, [
    dentalProcedures,
    isSaved,
    currentNumAmalgamFillings,
    amalgamTotalTeeth,
    dentalItems,
  ])

  return {
    adjustedAmalgamFillings,
    adjustedTotalAmalgamFillings,
    hasAmalgamAdjustments,
    hasTotalAmalgamAdjustments,
  }
}

export default useAmalgamAdjustments
